import _ from 'lodash';
import React from 'react';

import { SERVICE_TYPES, DRUG_TYPES } from 'Containers/ResultPage/constants';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useTextContext } from 'Contexts/textContext';
import Plan from 'Models/plan';
import { HealthCoverage } from 'Types/entities';

import CopayInfo from './CopayInfo';
import { CompareTable } from '../_shared/CompareTable';
import { CompareTableRow } from '../_shared/CompareTableRow';
import { NetworkTier } from '../types';

export interface CopaysProps {
  plans: Plan[];
  getSelectedTier: (plan: Plan) => NetworkTier;
}

export const Copays = ({ plans, getSelectedTier }: CopaysProps) => {
  const { retrieveContentfulData } = useTextContext();
  const { is_network_tier_toggle_enabled } = useFeatureFlagContext();

  if (!plans.length) {
    return null; // Prevent rendering if no plans exist
  }

  const firstPlan = plans[0] || null;
  const secondPlan = plans[1] || null;
  const thirdPlan = plans[2] || null;

  const header = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.headers.copays',
  );

  const getTierCoverageOverride = (
    plan: Plan,
    copayType: DRUG_TYPES | SERVICE_TYPES,
  ): HealthCoverage | null | undefined => {
    if (!is_network_tier_toggle_enabled) return undefined;
    const tier = getSelectedTier(plan);
    const hasMostPreferred = plan.hasMostPreferredNetwork;
    const defaultTier: NetworkTier = hasMostPreferred ? 'most_preferred' : 'in_network';

    // No override needed when selected tier is the plan's base tier
    if (tier === defaultTier) return undefined;

    if (tier === 'out_of_network') {
      if (copayType === SERVICE_TYPES.PRIMARY_CARE && plan.primaryCareTiers) {
        return plan.primaryCareTiers.out_of_network;
      }
      if (copayType === SERVICE_TYPES.SPECIALIST_CARE && plan.specialistCareTiers) {
        return plan.specialistCareTiers.out_of_network;
      }
      return undefined;
    }

    // comm-api bug: most_preferred and in_network keys are swapped in *_care_tiers for MP plans.
    const lookupTier: NetworkTier = hasMostPreferred
      ? tier === 'most_preferred'
        ? 'in_network'
        : tier === 'in_network'
          ? 'most_preferred'
          : tier
      : tier;

    if (copayType === SERVICE_TYPES.PRIMARY_CARE && plan.primaryCareTiers) {
      return plan.primaryCareTiers[lookupTier] ?? undefined;
    }
    if (copayType === SERVICE_TYPES.SPECIALIST_CARE && plan.specialistCareTiers) {
      return plan.specialistCareTiers[lookupTier] ?? undefined;
    }
    return undefined;
  };

  const shouldRenderRow = (isDrug: boolean, plans: Plan[], copayType: DRUG_TYPES | SERVICE_TYPES) => {
    if (isDrug) {
      const somePlanIsImputed = plans.some((plan) => plan[copayType as DRUG_TYPES]?.imputed);

      // avoid "null% coinsurance"
      const allPlansNoCopayAndNoCoinsurance = plans.every(
        (plan) => plan[copayType]?.copay === null && plan[copayType]?.coinsurance === null,
      );

      // do NOT render row if any imputed plans or they all have no copay/coinsurance
      if (somePlanIsImputed || allPlansNoCopayAndNoCoinsurance) {
        return false;
      }
    }
    return true;
  };

  const renderCopayFields = (isDrug: boolean) => {
    const copayTypes = isDrug ? DRUG_TYPES : SERVICE_TYPES;

    return Object.values(copayTypes).map((copayType: DRUG_TYPES | SERVICE_TYPES, index) => {
      const copayTitle = retrieveContentfulData<string>(
        `health_section.compare_plan_text.compareModal.terms.${copayType}.title`,
      );
      const copayTooltip = retrieveContentfulData<string>(`tool_tips.${_.snakeCase(copayType)}_copay.text`);

      const renderPlanInfo = (plan: Plan | null) =>
        plan ? (
          <CopayInfo
            plan={plan}
            copayType={copayType}
            perVisit={!isDrug}
            coverageOverride={getTierCoverageOverride(plan, copayType)}
            key={`copay-third-plan-${index}`}
          />
        ) : null;

      if (shouldRenderRow(isDrug, plans, copayType)) {
        return (
          <CompareTableRow
            rowHeader={copayTitle}
            rowHeaderTooltip={copayTooltip}
            columnOne={renderPlanInfo(firstPlan)}
            columnTwo={renderPlanInfo(secondPlan)}
            columnThree={renderPlanInfo(thirdPlan)}
            key={`compare-table-${index}`}
            keyHint={`compare-table-${index}`}
          />
        );
      }

      return null;
    });
  };

  return (
    <CompareTable tableHeader={header}>
      {renderCopayFields(false)}
      {renderCopayFields(true)}
    </CompareTable>
  );
};
